
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, CheckCircle, MailIcon, Loader2 } from "lucide-react";
import { 
  getWaitlistNotificationStats, 
  triggerWaitlistNotification 
} from "@/utils/supabase/waitlist";

export const WaitlistNotificationPanel = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState<{ 
    total: number | null;
    notified: number | null;
    pending: number | null;
  } | null>(null);
  
  // Load waitlist statistics on mount
  useEffect(() => {
    const loadStats = async () => {
      const statsData = await getWaitlistNotificationStats();
      setStats(statsData);
    };
    
    loadStats();
  }, []);

  const handleSendNotifications = async () => {
    if (!stats?.pending || stats.pending === 0) {
      return;
    }

    // Confirm before sending to multiple users
    if (stats.pending > 5) {
      if (!window.confirm(`Are you sure you want to send notifications to ${stats.pending} waitlist users?`)) {
        return;
      }
    }

    setIsLoading(true);
    
    try {
      const result = await triggerWaitlistNotification();
      
      if (result.success) {
        // Refresh stats
        const statsData = await getWaitlistNotificationStats();
        setStats(statsData);
      } else {
        console.error(`Failed to send waitlist notifications: ${result.error}`);
      }
    } catch (error: any) {
      console.error(`An error occurred: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Waitlist Launch Notifications</CardTitle>
        <CardDescription>
          Notify users on the waitlist that the platform is now live
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {stats ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-muted rounded-md">
              <div className="text-sm font-medium text-muted-foreground mb-1">Total Users</div>
              <div className="text-2xl font-bold">{stats.total || 0}</div>
            </div>
            <div className="p-4 bg-muted rounded-md">
              <div className="text-sm font-medium text-muted-foreground mb-1">Notified</div>
              <div className="text-2xl font-bold">{stats.notified || 0}</div>
            </div>
            <div className="p-4 bg-muted rounded-md">
              <div className="text-sm font-medium text-muted-foreground mb-1">Pending</div>
              <div className="text-2xl font-bold">{stats.pending || 0}</div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {stats?.pending === 0 && (
          <Alert className="bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border-green-200 dark:border-green-800">
            <CheckCircle className="h-4 w-4" />
            <AlertTitle>All caught up!</AlertTitle>
            <AlertDescription>
              All waitlist users have been notified about the platform launch.
            </AlertDescription>
          </Alert>
        )}

        {stats?.pending && stats.pending > 0 && (
          <Alert className="bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Pending Notifications</AlertTitle>
            <AlertDescription>
              There {stats.pending === 1 ? 'is' : 'are'} {stats.pending} user{stats.pending !== 1 ? 's' : ''} on the waitlist who {stats.pending === 1 ? 'hasn\'t' : 'haven\'t'} been notified about the platform launch.
            </AlertDescription>
          </Alert>
        )}

        <Button
          onClick={handleSendNotifications}
          disabled={isLoading || !stats?.pending}
          className="w-full sm:w-auto mt-4"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending Notifications...
            </>
          ) : (
            <>
              <MailIcon className="mr-2 h-4 w-4" />
              Send Waitlist Notifications
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default WaitlistNotificationPanel;
